import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { editorStore } from "../../store/editorStore";
import { Tooltip } from "flowbite-react";
import { PiMinus, PiPlus } from "react-icons/pi";
import { FaArrowRight } from "react-icons/fa6";
import { observer } from "mobx-react-lite";
import { ApolloError, ApolloQueryResult, gql } from "@apollo/client";
import {
  AddUserItemDocument,
  AddUserItemToNewMoodBoardDocument,
  UserDocumentsQuery,
  useUserDocumentsQuery,
} from "../../gql";
import { dataUrlToFile } from "../../utils/dataUrlToFile";
import Clarity from "@microsoft/clarity";
import { capturePosthogEvent } from "../../utils/posthog";
import { useExportPreviewSnapshot } from "../../hooks/useExportPreviewSnapshot";
import { useExportPreviewState } from "../../hooks/useExportPreviewState";
import { EXPORT_FORMAT_OPTIONS, ExportFormatId } from "../../utils/exportFormats";
import {
  collectPlacedCatalogProducts,
  getPlacedCatalogProductIds,
} from "../../features/floorplan3d/products/collectPlacedCatalogProducts";
import { isUserModelProductListId } from "../../features/floorplan3d/userModels/userModel3DCommerce";
import { buildAddFloorplanUserItemVariables } from "./buildAddFloorplanUserItemVariables";

const ADD_TO_PRESENTATION_FORMAT_OPTIONS = EXPORT_FORMAT_OPTIONS.filter(
  (format) => !["a4-portrait", "a4-landscape", "a3-portrait", "a3-landscape"].includes(format.id),
);

interface DropdownProps {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

export const AddToPresentation: React.FC<DropdownProps> = observer(({ onClose, dropdownRef }) => {
  const { t } = useTranslation();
  const {
    selectedFormatId,
    selectedFormat,
    setFormatId,
    zoom,
    pan,
    previewSize,
    drawingPreviewSize,
    drawablePixelSize,
    captureMetrics,
    handleWheelZoom,
    handleZoomStep,
    handlePan,
  } = useExportPreviewState({
    defaultFormatId: "desktop",
  });
  const [wizardStep, setWizardStep] = useState(1);
  const [isInitializing, setIsInitializing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** When on, placed catalog products are linked onto the new presentation page. */
  const [includeProductList, setIncludeProductList] = useState(false);
  const placedProductIds = getPlacedCatalogProductIds(floorplannerStore);
  const hasPlacedProducts = placedProductIds.length > 0;
  const hasUserModelsInProductList = collectPlacedCatalogProducts(
    floorplannerStore,
  ).some(
    (entry) =>
      entry.source === "userModel" || isUserModelProductListId(entry.productId),
  );

  useExportPreviewSnapshot({
    selectedFormatId,
    selectedFormat,
    drawablePixelSize,
    setIsInitializing,
  });

  const imgRef = useRef<HTMLDivElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
  const { data, error, refetch: refetchUserDocuments } = useUserDocumentsQuery({
    fetchPolicy: "cache-and-network",
  });
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (error) {
      console.error("Error fetching user documents:", error);
    }
  }, [error]);

  const handleNextStep = async (event: React.FormEvent | React.MouseEvent) => {
    event.preventDefault();

    setErrorMessage(null);

    if (wizardStep === 1) {
      renderStore.setRendering(true);

      try {
        if (editorStore.camera) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          const target = {
            orientation: selectedFormat.orientation,
            format: selectedFormat.format,
            width: selectedFormat.width,
            height: selectedFormat.height,
          };

          if (editorStore.view3D) {
            await renderStore.capture3DSnapshot("upload", target, editorStore.camera, undefined, {
              pan,
              zoom,
              previewSize,
            });
          } else {
            await renderStore.captureSnapshot(
              "upload",
              target,
              captureMetrics?.captureBox,
              editorStore.camera,
              undefined,
            );
          }
        }

        setWizardStep(2);
      } finally {
        renderStore.setRendering(false);
      }
    } else if (wizardStep === 2) {
      setRedirecting(true);

      if (!renderStore.imageData) {
        console.error("No image data available");
        onClose();
        return;
      }

      if (!selectedDocument) {
        console.error("No project selected");
        onClose();
        return;
      }

      const imageWidth = (renderStore as any).imageDataWidth;
      const imageHeight = (renderStore as any).imageDataHeight;
      const targetWidth = (renderStore as any).targetWidth;
      const targetHeight = (renderStore as any).targetHeight;

      const fallbackWidth = imageWidth || targetWidth || 1754;
      const fallbackHeight = imageHeight || targetHeight || 1240;
      const isHighRes =
        imageWidth > (targetWidth || fallbackWidth) ||
        imageHeight > (targetHeight || fallbackHeight);
      // Match the previous behaviour: high-res preserves the raw render dims,
      // standard exports clamp to the target/format dimensions.
      const renderWidth = isHighRes ? imageWidth : fallbackWidth;
      const renderHeight = isHighRes ? imageHeight : fallbackHeight;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) {
        console.error("Failed to get canvas context");
        onClose();
        return;
      }

      canvas.width = renderWidth;
      canvas.height = renderHeight;
      context.fillStyle = "white";
      context.fillRect(0, 0, renderWidth, renderHeight);

      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            context.drawImage(img, 0, 0, renderWidth, renderHeight);
            resolve(canvas.toDataURL("image/png"));
          };
          img.onerror = () => reject(new Error("Failed to load image data"));
          img.src = renderStore.imageData!;
        });

        const file = await dataUrlToFile(dataUrl, `floorplan_${Date.now()}.png`);
        const productIds =
          includeProductList && hasPlacedProducts ? placedProductIds : [];

        const addUserItemResult = await renderStore.client?.mutate({
          mutation: AddUserItemDocument,
          variables: buildAddFloorplanUserItemVariables({ image: file, productIds }),
        });

        const userItemId = addUserItemResult?.data?.addUserItem?.id;
        if (!userItemId) {
          throw new Error("Failed to create user item from floorplan image");
        }

        const addToBoardResult = await renderStore.client?.mutate({
          mutation: AddUserItemToNewMoodBoardDocument,
          variables: {
            documentId: selectedDocument.id,
            userItemId,
          },
        });

        const newMoodBoard = addToBoardResult?.data?.addUserItemToNewMoodBoard;
        if (newMoodBoard) {
          Clarity.event("add_to_presentation");
          capturePosthogEvent("add_to_presentation");
          window.location.href = `${process.env.REACT_APP_CLIENT_URI}/editor/${selectedDocument.id}#scroll-last-page`;
        } else {
          throw new Error("Failed to add floorplan page to project");
        }
      } catch (error) {
        console.error("Error adding floorplan to project:", error);
        const message =
          error instanceof ApolloError
            ? (error.graphQLErrors[0]?.message ?? error.message)
            : error instanceof Error
              ? error.message
              : "Something went wrong. Please try again.";
        setErrorMessage(message);
        setRedirecting(false);
      }
    }
  };

  return (
    <div
      className="absolute z-50 w-[600px] overflow-y-auto rounded border border-gray-300 bg-[#EDEBE6] shadow-md top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      ref={dropdownRef}
      onWheel={(event) => event.stopPropagation()}
    >
      <div className="flex flex-cols items-center justify-between p-2">
        <div className="text-sm font-normal">{t("common.addToProject")}</div>
        <div onClick={onClose} className="cursor-pointer text-sm text-gray-500">
          <IoClose style={{ strokeWidth: 0.5 }} size={24} />
        </div>
      </div>
      <div className="w-full border-b border-gray-300" />

      {wizardStep === 1 && (
        <>
          <div className="mt-2 flex w-full flex-col items-center justify-between px-10">
            <div className="w-full text-md font-normal">{t("common.selectArea")}</div>
            <div className="w-full text-sm text-gray-500">{t("common.zoomAreaAddHint")}</div>

            {isInitializing ? (
              <div className="flex w-full items-center justify-center p-8" style={{ height: `${previewSize.height}px` }}>
                <div className="mr-3 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-600" />
                <span className="text-gray-600">{t("common.initializingPreview")}</span>
              </div>
            ) : (
              <div className="mt-2 flex w-full flex-row items-center justify-center">
                <div className="rounded-sm mt-4 overflow-hidden rounded border border-gray-300 bg-white shadow-md">
                  <div
                    className="relative overflow-hidden bg-white"
                    style={{
                      cursor: "grab",
                      width: `${previewSize.width}px`,
                      height: `${previewSize.height}px`,
                    }}
                    onWheel={handleWheelZoom}
                    onMouseMove={handlePan}
                  >
                    <div
                      className="absolute"
                      ref={imgRef}
                      style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: "center center",
                        width: drawingPreviewSize.width,
                        height: previewSize.height,
                      }}
                    >
                      <img
                        src={renderStore.imageData}
                        alt={floorplannerStore.name}
                        className="h-full w-full object-contain"
                        draggable={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="ml-2 flex flex-col space-y-2">
                  <Tooltip content={t("common.zoomIn")} placement="right">
                    <button
                      onClick={() => handleZoomStep(0.1)}
                      className="w-full rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100"
                    >
                      <PiPlus size={20} />
                    </button>
                  </Tooltip>
                  <Tooltip content={t("common.zoomOut")} placement="right">
                    <button
                      onClick={() => handleZoomStep(-0.1)}
                      className="w-full rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100"
                    >
                      <PiMinus size={20} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>

          <form className="flex h-full w-full flex-col items-center justify-between px-10" onSubmit={handleNextStep}>
            <div className="flex flex-cols items-start justify-between p-2 w-full gap-4">
              <div className="w-full text-sm text-gray-500">
                <label htmlFor="resolution">{t("common.format")}</label>
                <br />
                <select
                  id="resolution"
                  name="resolution"
                  className="mt-2 w-full"
                  value={selectedFormatId}
                  onChange={(event) => setFormatId(event.target.value as ExportFormatId)}
                >
                  {ADD_TO_PRESENTATION_FORMAT_OPTIONS.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              id="capture"
              type="submit"
              className={
                renderStore.rendering
                  ? "mb-4 mt-4 w-full cursor-not-allowed rounded-md bg-gray-600 p-4 text-white"
                  : "mb-4 mt-4 w-full rounded-md bg-black p-4 text-white"
              }
              disabled={renderStore.rendering}
            >
              <div className="flex flex-row items-center justify-center">
                {renderStore.rendering && <div className="mr-3 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />}
                {!renderStore.rendering && <>{t("common.chooseProject")}</>}
                &nbsp; <FaArrowRight className="text-xl transform scale-90" />
              </div>
            </button>
          </form>
        </>
      )}

      {wizardStep === 2 && (
        <div className="mt-2 flex w-full flex-col items-center justify-between px-10">
          <div className="w-full text-md font-normal">{t("common.chooseProject")}</div>
          <div className="w-full text-sm text-gray-500">{t("addToPresentation.clickProject")}</div>

          <div className="mt-2 grid h-[350px] w-full grid-cols-3 gap-2 overflow-scroll">
            {data?.userDocuments?.documents?.map((document: any) => (
              <DocumentItem
                key={document.id}
                document={document}
                selectDocument={(nextDocument) => setSelectedDocument(nextDocument)}
                selected={selectedDocument?.id === document.id}
                refetchUserDocuments={refetchUserDocuments}
              />
            ))}
          </div>
          {hasPlacedProducts && (
            <div className="mt-3 w-full px-0.5">
              {hasUserModelsInProductList ? (
                <Tooltip
                  content={t("addToPresentation.userModelsProductListHint")}
                  placement="top"
                  style="dark"
                  className="max-w-[240px] text-left text-[11px] leading-snug"
                >
                  <label
                    htmlFor="add-to-presentation-include-product-list"
                    className="flex cursor-pointer items-center gap-3 text-sm text-gray-900"
                  >
                    <input
                      id="add-to-presentation-include-product-list"
                      type="checkbox"
                      checked={includeProductList}
                      onChange={(event) =>
                        setIncludeProductList(event.target.checked)
                      }
                      className="h-5 w-5 rounded-sm border border-gray-400 bg-white text-black focus:ring-0"
                    />
                    <span>
                      {t("addToPresentation.includeProductList", {
                        count: placedProductIds.length,
                      })}
                    </span>
                  </label>
                </Tooltip>
              ) : (
                <label
                  htmlFor="add-to-presentation-include-product-list"
                  className="flex cursor-pointer items-center gap-3 text-sm text-gray-900"
                >
                  <input
                    id="add-to-presentation-include-product-list"
                    type="checkbox"
                    checked={includeProductList}
                    onChange={(event) =>
                      setIncludeProductList(event.target.checked)
                    }
                    className="h-5 w-5 rounded-sm border border-gray-400 bg-white text-black focus:ring-0"
                  />
                  <span>
                    {t("addToPresentation.includeProductList", {
                      count: placedProductIds.length,
                    })}
                  </span>
                </label>
              )}
              {hasUserModelsInProductList ? (
                <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
                  {t("addToPresentation.userModelsProductListHint")}
                </p>
              ) : null}
            </div>
          )}
          <button
            type="submit"
            className={
              selectedDocument === null || redirecting
                ? "mb-4 mt-4 w-full cursor-not-allowed rounded-md bg-gray-600 p-4 text-white"
                : "mb-4 mt-4 w-full rounded-md bg-black p-4 text-white"
            }
            disabled={selectedDocument === null || redirecting}
            onClick={handleNextStep}
          >
            <div className="flex flex-row items-center justify-center">
              {redirecting && <div className="mr-3 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />}
              {!redirecting && <>{t("common.addToProject")}</>}
              &nbsp; <FiDownload className="text-xl transform scale-90" />
            </div>
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="mx-10 mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="mt-0.5 shrink-0">⚠</span>
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
});

export const DocumentItem: React.FC<{
  document: any;
  selectDocument?: (document: any) => void;
  selected?: boolean;
  refetchUserDocuments?: () => Promise<ApolloQueryResult<UserDocumentsQuery>>;
}> = ({ document, selectDocument, selected }) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        "relative flex h-[105px] w-[165px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md shadow-md" +
        (selected ? " border-2 border-boniYellowPrimary" : " border border-gray-300")
      }
      onClick={() => {
        selectDocument && selectDocument(document);
      }}
    >
      <div className="relative flex h-[81px] w-[165px] flex-col">
        <img
          className="h-[148px] w-full object-contain bg-white hover:border-gray-700"
          src={
            document?.image && document?.image.length > 0
              ? document?.image
              : `${process.env.PUBLIC_URL}/EmptyProjectBackground.png`
          }
          style={{ objectFit: "cover" }}
          alt="BONI Board Preview Snapshot"
        />
      </div>
      <div className="flex h-[22px] w-full flex-row items-center justify-between bg-[#F7F6F0CC] pl-[16px] py-[13px] font-dm text-[14px] font-[400] backdrop-blur-[2px]">
        <p>{document.name === "Name Your BONI Project" ? t("common.untitled") : document.name}</p>
      </div>
    </div>
  );
};

gql`
  query userDocuments($input: UserDocumentsQueryInput) {
    userDocuments(input: $input) {
      documents {
        id
        name
        image
        updatedAt
        moodBoards {
          id
          name
          image
          updatedAt
        }
      }
      total
    }
  }
`;
